// apps/web/src/components/threat-actors/ThreatActorContextRail.tsx
//
// KAN-221 — right rail (wide screens). Source coverage summarizes the loaded view
// rather than duplicating the list, and is labelled "loaded view" because the
// list is capped at 50 server-side. Attribution mix is intentionally NOT
// repeated here (it lives in the corpus-wide pulse strip). The speculative
// "planned signals / future API" block from the design mock is omitted as too
// speculative for production.

import { useId, type ReactNode } from 'react'
import type { ThreatActor } from '@/lib/types'
import { sourceCoverage } from './threat-actor-utils'

function RailPanel({
  label,
  aux,
  children,
}: {
  label: string
  aux?: ReactNode
  children: ReactNode
}) {
  const id = useId()
  return (
    <section aria-labelledby={id} className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3
          id={id}
          className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
        >
          {label}
        </h3>
        {aux ? (
          <span className="font-mono text-[10px] text-muted-foreground/70">{aux}</span>
        ) : null}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  )
}


export interface ThreatActorContextRailProps {
  actors?: ThreatActor[]
}

export function ThreatActorContextRail({ actors }: ThreatActorContextRailProps) {
  const loaded = actors ?? []
  if (loaded.length === 0) return null


  const sources = sourceCoverage(loaded)
  const sourceMax = Math.max(...sources.map((s) => s.count), 1)

  return (
    <aside className="grid content-start gap-4 sm:grid-cols-2 xl:grid-cols-1">
      <section className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">
          Analyst note
        </h3>
        <p className="mt-2 text-[12.5px] leading-relaxed text-muted-foreground">
          Attribution reflects published reporting. Weigh the linked profiles alongside class
          before reaching an assessment.
        </p>
      </section>


      <RailPanel label="Source coverage" aux="loaded view">
        <ul className="list-none">
          {sources.map((s) => (
            <li key={s.label} className="mb-2 flex items-center gap-2.5 last:mb-0">
              <span className="w-16 shrink-0 font-mono text-[10px] font-semibold text-foreground/80">
                {s.label}
              </span>
              <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full bg-success/75"
                  style={{ width: `${(s.count / sourceMax) * 100}%` }}
                />
              </span>
              <span className="w-7 shrink-0 text-right font-mono text-[11px] text-muted-foreground">
                {s.count}
              </span>
            </li>
          ))}
        </ul>
      </RailPanel>
    </aside>
  )
}
